import { BaseModal } from "@/components/modal/base-modal";
import LabelContainer from "@/components/form/hook-form/label-container";
import { cn } from "@/utils/cn";
import { textVariants } from "@/utils/text-variants";
import { useViewportHeight } from "@/hooks/utils/use-viewport-height";
import { IonIcon } from "@ionic/react";
import { chevronDownOutline } from "ionicons/icons";
import { format } from "date-fns";
import { useState } from "react";
import { FieldValues, Path, PathValue, UseFormReturn, useWatch } from "react-hook-form";
import { BirthdayConfirmationModal } from "../modal/birthday-confirmation-modal";
import { DateSelectModal } from "./date-select-modal";

type DateModalSelectProps<T extends FieldValues> = {
  form: UseFormReturn<T>;
  name: Path<T>;
  error?: string;
  label?: string;
  theme?: "light" | "settings";
};

function formatDateLabel(value?: string) {
  if (!value) return "";
  try {
    return format(new Date(value), "dd MMM yyyy");
  } catch {
    return value;
  }
}

export const DateModalSelect = <T extends FieldValues>({
  form,
  name,
  error,
  label = "Birthday",
  theme = "light",
}: DateModalSelectProps<T>) => {
  const isSettings = theme === "settings";
  const watchedValue = useWatch({ control: form.control, name });
  const displayLabel = formatDateLabel(watchedValue);
  const viewportHeight = useViewportHeight();

  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [pendingBirthday, setPendingBirthday] = useState<string>("");

  // "Confirm Birthday" in the picker is the save action: the date is written
  // into the form right away. The "Woo-hoo!" dialog that follows is only a
  // celebration — "Thanks!", the back button and tapping outside all just close
  // it, and "Edit" reopens the picker to change the date.
  const handleDateSelect = (val: string) => {
    form.setValue(name, val as PathValue<T, Path<T>>, {
      shouldValidate: true,
      shouldDirty: true,
    });
    setPendingBirthday(val);
    setIsPickerOpen(false);
    setShowConfirmation(true);
  };

  const handleCloseConfirmation = () => {
    setShowConfirmation(false);
    setPendingBirthday("");
  };

  const handleEditBirthday = () => {
    setShowConfirmation(false);
    setIsPickerOpen(true);
  };

  return (
    <>
      <LabelContainer label={label} htmlFor={name} error={error} theme={theme}>
        <button
          id={name}
          type="button"
          onClick={() => setIsPickerOpen(true)}
          className={cn(
            "h-11.5 w-full rounded-xl border border-[var(--color-border-input-default)] bg-white px-3 text-left text-base",
            "flex items-center justify-between",
            "transition-colors duration-150",
            isSettings &&
              "h-[52px] rounded-[27px] border-[#ebe0fb] px-[18px] shadow-[0px_8px_64.7px_0px_rgba(64,0,255,0.1)]",
          )}
        >
          <span
            className={cn(
              isSettings ? textVariants.inputText : textVariants.body,
              displayLabel ? "text-black" : "text-text-placeholder-default",
            )}
          >
            {displayLabel || "Choose..."}
          </span>
          <IonIcon
            icon={chevronDownOutline}
            className={cn("transition-transform duration-150", { "rotate-180": isPickerOpen })}
          />
        </button>
      </LabelContainer>

      <BaseModal
        isOpen={isPickerOpen}
        onDismiss={() => setIsPickerOpen(false)}
        initialBreakpoint={390 / viewportHeight}
        breakpoints={[0, 390 / viewportHeight]}
        scrollY={false}
        forceOverscroll={false}
        styles={{ content: { overflow: "hidden" } }}
      >
        <DateSelectModal
          defaultValue={
            pendingBirthday || form.getValues(name) || new Date().toISOString().slice(0, 10)
          }
          onSelect={handleDateSelect}
        />
      </BaseModal>

      <BirthdayConfirmationModal
        isOpen={showConfirmation}
        onDismiss={handleCloseConfirmation}
        birthday={pendingBirthday}
        onConfirm={handleCloseConfirmation}
        onEdit={handleEditBirthday}
      />
    </>
  );
};

export default DateModalSelect;
